import { Chip, IconButton, Tooltip } from '@mui/material';
import { AlertCircle, ListTree, Loader2, Paperclip } from 'lucide-react';
import type { AttachmentDraft } from '../../hooks';
import styles from './AttachmentChipsRow.module.scss';

interface AttachmentChipsRowProps {
  drafts: AttachmentDraft[];
  onRemove: (localId: string) => void;
  onEditHeaders?: (draft: AttachmentDraft) => void;
}

const renderIcon = (status: AttachmentDraft['status']) => {
  if (status === 'uploading' || status === 'parsing') {
    return (
      <span className={styles.spinner}>
        <Loader2 size={12} />
      </span>
    );
  }
  if (status === 'error') return <AlertCircle size={12} />;
  return <Paperclip size={12} />;
};

const renderLabel = (d: AttachmentDraft): string => {
  if (d.status === 'uploading') return `${d.name} · ${d.progress}%`;
  if (d.status === 'parsing') return `${d.name} · ${d.progressLabel ?? 'парсится…'}`;
  if (d.status === 'error') return `${d.name} · ошибка`;
  if (d.selectedHeaders && d.selectedHeaders.length > 0)
    return `${d.name} · ${d.selectedHeaders.length} разд.`;
  return d.name;
};

const AttachmentChipsRow = ({ drafts, onRemove, onEditHeaders }: AttachmentChipsRowProps) => {
  if (drafts.length === 0) return null;
  return (
    <div className={styles.row}>
      {drafts.map((d) => {
        const chip = (
          <Chip
            size="small"
            variant="outlined"
            color={d.status === 'error' ? 'error' : 'default'}
            icon={renderIcon(d.status)}
            label={renderLabel(d)}
            onDelete={() => onRemove(d.localId)}
            className={styles.chip}
          />
        );
        const editButton =
          d.status === 'ready' && d.documentId && onEditHeaders ? (
            <Tooltip key={`${d.localId}-headers`} title="Выбрать разделы документа">
              <IconButton size="small" onClick={() => onEditHeaders(d)}>
                <ListTree size={14} />
              </IconButton>
            </Tooltip>
          ) : null;
        if (d.status === 'error' && d.error) {
          return (
            <Tooltip key={d.localId} title={d.error}>
              {chip}
            </Tooltip>
          );
        }
        return (
          <span key={d.localId} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {chip}
            {editButton}
          </span>
        );
      })}
    </div>
  );
};

export default AttachmentChipsRow;
